import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, MessageCircle, Home, Mail } from 'lucide-react';
import SEO from '../components/SEO';
import company from '../config/company';

/**
 * Dedicated thank-you page reached after ANY successful lead submission.
 *
 * Purpose:
 *   - Single stable URL (/bedankt) so Google Ads can register a conversion
 *     when the user lands here, regardless of which form they filled in
 *     (property detail, homepage capture, exit-intent popup, contact page…).
 *   - Friendly, on-brand message reassuring the visitor that an advisor will
 *     reach out — turns a transactional moment into a relationship signal.
 *
 * SEO:
 *   noindex'd via the SEO component so it never shows up in organic results
 *   (Google Ads conversion pages should never compete in SERP).
 *
 * Tracking:
 *   Pushes a single `conversion` dataLayer event on mount. The Google Ads
 *   conversion tag in GTM can listen to this OR simply match the URL pattern
 *   /bedankt — both work; the dataLayer push gives you the cleaner GTM trigger.
 */
const ThankYou = () => {
  useEffect(() => {
    if (typeof window === 'undefined' || !window.dataLayer) return;
    window.dataLayer.push({
      event: 'conversion',
      conversion_type: 'lead_submitted',
      page_path: '/bedankt',
    });
    // Google Ads global tag — fires the conversion via gtag if it's loaded
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'conversion', { send_to: 'lead_submitted' });
    }
  }, []);

  return (
    <div style={styles.page} data-testid="thankyou-page">
      <SEO
        titleKey="seo.contactTitle"
        descriptionKey="seo.contactDescription"
        noindex
      />
      <div style={styles.container}>
        <div style={styles.iconWrap}>
          <CheckCircle2 size={56} color="#22C55E" />
        </div>

        <h1 style={styles.title} data-testid="thankyou-title">
          Dziękujemy za Twoje zgłoszenie!
        </h1>

        <p style={styles.lead}>
          Jeden z naszych doradców skontaktuje się z Tobą osobiście jak najszybciej. Zwykle w ciągu 24 godzin, często znacznie szybciej.
        </p>

        <div style={styles.intlNote} data-testid="thankyou-intl-note">
          <span style={styles.intlTag}>ES</span>
          <span>
            <strong>Nasz międzynarodowy zespół w Hiszpanii skontaktuje się z Tobą po angielsku.</strong><br />
            <span style={styles.intlNoteEn}>Our international team in Spain will contact you in English.</span>
          </span>
        </div>

        <div style={styles.checkList}>
          <Item
            text="Analizujemy Twoje potrzeby i przygotowujemy spersonalizowaną selekcję."
          />
          <Item
            text="Otrzymasz odpowiedź przez e-mail, telefon lub WhatsApp — jak wolisz."
          />
          <Item
            text="W razie potrzeby od razu zaplanujemy oglądanie nieruchomości w Hiszpanii."
          />
        </div>

        <div style={styles.cta}>
          <a
            href={company.whatsappUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={styles.btnWhatsapp}
            data-testid="thankyou-whatsapp"
          >
            <MessageCircle size={18} />
            Bezpośredni kontakt przez WhatsApp
          </a>
          <a
            href={`mailto:${company.email}`}
            style={styles.btnEmail}
            data-testid="thankyou-email"
          >
            <Mail size={18} />
            {company.email}
          </a>
        </div>

        <div style={styles.divider} />

        <p style={styles.subnote}>
          W międzyczasie zapraszamy do dalszego przeglądania naszej oferty:
        </p>
        <div style={styles.links}>
          <Link to="/woningen" style={styles.linkBtn} data-testid="thankyou-back-woningen">
            <Home size={16} /> Zobacz wszystkie nieruchomości
          </Link>
          <Link to="/" style={styles.linkMuted}>
            Powrót na stronę główną
          </Link>
        </div>
      </div>
    </div>
  );
};

const Item = ({ text }) => (
  <div style={styles.checkItem}>
    <CheckCircle2 size={16} color="#C5A059" style={{ marginTop: '2px', flexShrink: 0 }} />
    <span>{text}</span>
  </div>
);

const styles = {
  intlNote: {
    display: 'flex', gap: '0.6rem', alignItems: 'flex-start', textAlign: 'left',
    padding: '0.75rem 1rem', background: '#F6F1E7', border: '1px solid #E7DCC4',
    margin: '0 auto 1.5rem', maxWidth: '520px', fontSize: '0.82rem', lineHeight: 1.5, color: '#585755',
  },
  intlTag: {
    flexShrink: 0, fontSize: '0.62rem', fontWeight: 700, letterSpacing: '0.05em',
    background: '#1A1918', color: '#C5A059', padding: '0.15rem 0.4rem', marginTop: '0.1rem',
  },
  intlNoteEn: { color: '#8C8B88', fontStyle: 'italic' },

  page: {
    minHeight: '100vh',
    backgroundColor: '#FAF9F6',
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    padding: '4rem 1.5rem',
  },
  container: {
    maxWidth: '620px',
    width: '100%',
    backgroundColor: 'white',
    padding: '3rem 2.5rem',
    boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
    borderTopWidth: '4px',
    borderTopStyle: 'solid',
    borderTopColor: '#C5A059',
  },
  iconWrap: { textAlign: 'center', marginBottom: '1.5rem' },
  title: {
    fontFamily: "'Playfair Display', serif",
    fontSize: 'clamp(1.8rem, 4vw, 2.6rem)',
    color: '#1A1918',
    textAlign: 'center',
    margin: '0 0 1rem',
    lineHeight: 1.2,
    fontWeight: 500,
  },
  lead: {
    color: '#585755',
    fontSize: '1.05rem',
    textAlign: 'center',
    lineHeight: 1.6,
    margin: '0 0 2rem',
  },
  checkList: {
    display: 'flex', flexDirection: 'column', gap: '0.65rem',
    backgroundColor: '#FAF9F6',
    padding: '1.25rem 1.5rem',
    marginBottom: '2rem',
    borderLeftWidth: '3px',
    borderLeftStyle: 'solid',
    borderLeftColor: '#C5A059',
  },
  checkItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '0.6rem',
    color: '#585755',
    fontSize: '0.95rem',
    lineHeight: 1.55,
  },
  cta: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.6rem',
    marginBottom: '2rem',
  },
  btnWhatsapp: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.5rem',
    backgroundColor: '#25D366',
    color: 'white',
    textDecoration: 'none',
    padding: '1rem 1.5rem',
    fontSize: '0.85rem',
    fontWeight: 700,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    fontFamily: "'Manrope', sans-serif",
  },
  btnEmail: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.5rem',
    backgroundColor: '#1A1918',
    color: 'white',
    textDecoration: 'none',
    padding: '1rem 1.5rem',
    fontSize: '0.88rem',
    fontWeight: 500,
    fontFamily: "'Manrope', sans-serif",
  },
  divider: {
    borderTopWidth: '1px',
    borderTopStyle: 'solid',
    borderTopColor: '#EAE5DD',
    margin: '0 -2.5rem 2rem',
  },
  subnote: {
    color: '#8C8B88',
    fontSize: '0.88rem',
    textAlign: 'center',
    marginBottom: '1rem',
  },
  links: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.6rem',
    alignItems: 'center',
  },
  linkBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.5rem',
    color: '#C5A059',
    textDecoration: 'none',
    fontSize: '0.92rem',
    fontWeight: 600,
    padding: '0.5rem 1rem',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: '#C5A059',
  },
  linkMuted: {
    color: '#8C8B88',
    textDecoration: 'underline',
    fontSize: '0.85rem',
  },
};

export default ThankYou;
